import React, { useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, RefreshCw, Navigation, Lock, Cloud, Users, Compass } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/utils/i18n";
import { resolveImageUrl } from "@/utils/imageUrl";
import useTabMusic from "@/hooks/useTabMusic";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SHARED_WORLD_LOCATIONS = [
  {
    name: "Il Cataclisma",
    desc_it: "Il centro dell'evento che ha frammentato il mondo. Instabilità magnetica e temporale costante.",
    desc_en: "The center of the event that fragmented the world. Constant magnetic and temporal instability.",
    npcs: ["L'Eco del Tempo", "Kael-9, Guardiano dei Relitti", "Mastra Vrena, la Singolarista", "Anomalia Svanente"],
    x: 50,
    y: 50,
    bg: "linear-gradient(135deg, #4c1d95 0%, #1e1b4b 100%)",
    iconColor: "#a78bfa"
  },
  {
    name: "Vette dei Draghi",
    desc_it: "Nidi di draghi sulle rovine dei piloti delle antiche astronavi planetarie.",
    desc_en: "Dragon nests built upon the ruins of ancient planetary starship pilots.",
    npcs: ["Kaida la Domatrice", "Tatsu, l'Eremita del Vento", "Skor, il Cacciatore di Nidi", "Artiglio di Cenere"],
    x: 30,
    y: 12,
    bg: "linear-gradient(135deg, #065f46 0%, #047857 100%)",
    iconColor: "#34d399"
  },
  {
    name: "Yggdrasil Minore",
    desc_it: "Albero colossale cresciuto sopra un reattore a fusione vegetale. Le foglie cantano, la linfa è radioattiva.",
    desc_en: "A colossal tree grown over a plant-based fusion reactor. The leaves sing, and the sap is radioactive.",
    npcs: ["Druido Kaelen", "Linfa-Verde Nyssa", "Brenn l'Eretico", "Custode Silente"],
    x: 30,
    y: 25,
    bg: "linear-gradient(135deg, #064e3b 0%, #022c22 100%)",
    iconColor: "#10b981"
  },
  {
    name: "Il Santuario dei Sussurri",
    desc_it: "Uno specchio olografico (IA) venerato come la voce della Foresta.",
    desc_en: "A holographic mirror (AI) worshipped as the voice of the Forest.",
    npcs: ["Aura l'Oracolo", "Scriba Varis", "Echo-3", "Liriel la Cercatrice"],
    x: 18,
    y: 32,
    bg: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)",
    iconColor: "#2dd4bf"
  },
  {
    name: "Le Rovine Sommerse di Oros",
    desc_it: "Un'antica metropoli coperta da paludi, ruderi e cablaggi ancora attivi.",
    desc_en: "An ancient metropolis covered by swamps, ruins, and still-active wiring.",
    npcs: ["Zael lo Scavatore", "Mire-Stalker Glog", "Dr. Raymond", "L'Annegata"],
    x: 28,
    y: 36,
    bg: "linear-gradient(135deg, #134e5e 0%, #71b280 100%)",
    iconColor: "#a7f3d0"
  },
  {
    name: "La Necropoli di Silicio",
    desc_it: "Distese infinite di server e computer impazziti che costituiscono vere e proprie città di ferro e attirano gli intrusi.",
    desc_en: "Infinite expanses of crazed servers and computers that form true cities of iron, attracting intruders.",
    npcs: ["Null-0", "Cablaggio Corrotto", "Sintetico Val-8", "Algoritmo Senza Nome"],
    x: 62,
    y: 15,
    bg: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    iconColor: "#94a3b8"
  },
  {
    name: "La Guglia di Vetro",
    desc_it: "Nucleo centrale di calcolo alto chilometri. Emette impulsi energetici casuali e imprevedibili.",
    desc_en: "A kilometer-tall central computing core. It emits random and unpredictable energy pulses.",
    npcs: ["Vectis l'Ingegnere", "Proiezione Alfa", "Cody, il Hacker", "Riflesso di Specchio"],
    x: 68,
    y: 24,
    bg: "linear-gradient(135deg, #0891b2 0%, #0369a1 100%)",
    iconColor: "#38bdf8"
  },
  {
    name: "I Giardini Sospesi di Neon",
    desc_it: "Serra fluttuante grande come una città. Flora modificata che emette luce propria.",
    desc_en: "A floating greenhouse as large as a city. Modified flora that emits its own light.",
    npcs: ["Iris la Botanica", "Spora Synt", "Il Giardiniere-04", "Flora la Mutata"],
    x: 74,
    y: 47,
    bg: "linear-gradient(135deg, #1e1b4b 0%, #311042 100%)",
    iconColor: "#f472b6"
  },
  {
    name: "Il Mare di Mercurio",
    desc_it: "Lago artificiale di nano-mercurio liquido. Altamente tossico, ma fonte di energia quasi inesauribile.",
    desc_en: "An artificial lake of liquid nano-mercury. Highly toxic, but an almost inexhaustible energy source.",
    npcs: ["Toxis l'Estrattore", "Argento Vivo", "Merc, il Navigatore", "Scrappy"],
    x: 80,
    y: 34,
    bg: "linear-gradient(135deg, #475569 0%, #334155 100%)",
    iconColor: "#cbd5e1"
  },
  {
    name: "La Città Galleggiante di Driftwood",
    desc_it: "Metropoli di navi e piattaforme riadattate. Cuore del commercio e del contrabbando.",
    desc_en: "A metropolis of repurposed ships and platforms. The heart of commerce and smuggling.",
    npcs: ["Capitano Thorne", "Bilge-Rat Toby", "Madame Vesper", "Barnaby, il Fabbro Navale"],
    x: 20,
    y: 58,
    bg: "linear-gradient(135deg, #78350f 0%, #451a03 100%)",
    iconColor: "#fbbf24"
  },
  {
    name: "Il Deserto di Vetro",
    desc_it: "Sabbia fusa in specchi giganti. Sotto il vetro, le strade di un alveare ormai sepolto.",
    desc_en: "Sand fused into giant mirrors. Beneath the glass, the streets of an hive city now buried.",
    npcs: ["Dune-Stalker Riza", "Silica-Rex", "Vash, il Collezionista di Specchi", "Glow-Bug"],
    x: 35,
    y: 72,
    bg: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
    iconColor: "#f97316"
  },
  {
    name: "La Fossa delle Sirene",
    desc_it: "Trincea oceanica da cui provengono segnali acustici distorti. Attrae i marinai verso la follia.",
    desc_en: "An oceanic trench emitting distorted acoustic signals. It lures sailors into madness.",
    npcs: ["Siren Echo-7", "Profondo Nero", "Dr. Lana", "Dory, il Drone Subacqueo"],
    x: 12,
    y: 78,
    bg: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
    iconColor: "#6366f1"
  },
  {
    name: "L'Isola del Faro Nero",
    desc_it: "Faro automatizzato che emette un raggio oscuro; disturba i radar e causa naufragi frequenti.",
    desc_en: "An automated lighthouse emitting a dark beam; it disrupts radars and causes frequent shipwrecks.",
    npcs: ["Gideon l'Eremita", "Spettro Radar", "Nadia la Naufraga", "Beacon-9"],
    x: 32,
    y: 83,
    bg: "linear-gradient(135deg, #450a0a 0%, #180000 100%)",
    iconColor: "#ef4444"
  },
  {
    name: "Cittadella di Ocrea",
    desc_it: "Capitale del regno umano. Costruita intorno a un antico scudo energetico. Mura di pietra e titanio antico.",
    desc_en: "Capital of the human kingdom. Built around an ancient energy shield. Stone and ancient titanium walls.",
    npcs: ["Lord Valerius", "Fabbro Marcus", "Dama Elene", "Capitano Drake", "Tito, lo Studioso"],
    x: 66,
    y: 69,
    bg: "linear-gradient(135deg, #1e3a8a 0%, #172554 100%)",
    iconColor: "#eab308"
  },
  {
    name: "Il Monastero dell'Algoritmo",
    desc_it: "Ordine di studiosi che tenta di tradurre gli antichi 'libri sacri' (manuali di codice e istruzioni dimenticate).",
    desc_en: "An order of scholars attempting to translate the ancient 'holy books' (code manuals and forgotten instructions).",
    npcs: ["Abate Eldrin", "Fratello Joshua", "Sorella Clara", "Codex"],
    x: 62,
    y: 81,
    bg: "linear-gradient(135deg, #b45309 0%, #78350f 100%)",
    iconColor: "#f59e0b"
  },
  {
    name: "La Fossa del Titano",
    desc_it: "Cratere perfetto che custodisce lo scheletro di un Mecha colossale. Nelle sue interiora è nata una città di minatori.",
    desc_en: "A perfect crater harboring the skeleton of a colossal Mecha. A city of miners was born within its internals.",
    npcs: ["Grom il Minatore", "Scavatore Jax", "Mastra Tess", "Rusty"],
    x: 85,
    y: 83,
    bg: "linear-gradient(135deg, #3f3f46 0%, #18181b 100%)",
    iconColor: "#a1a1aa"
  },
  {
    name: "Il Passo del Fulmine",
    desc_it: "Gola montana con antiche turbine d'energia che scatenano tempeste di fulmini costanti, bloccando ogni passaggio.",
    desc_en: "A mountain pass with ancient energy turbines that unleash constant lightning storms, blocking all passage.",
    npcs: ["Storm-Weaver Vanya", "Fulmine", "Garrick il Guardiano", "Tesla-Drone"],
    x: 88,
    y: 56,
    bg: "linear-gradient(135deg, #1e1b4b 0%, #3730a3 100%)",
    iconColor: "#a5b4fc"
  },
  {
    name: "Mare del Nord",
    desc_it: "Correnti instabili e anomalie magnetiche.",
    desc_en: "Unstable currents and magnetic anomalies.",
    npcs: ["Capitano Erik", "Njord il Marinaio", "Echo-9"],
    x: 50,
    y: 15,
    bg: "linear-gradient(135deg, #1e3a8a 0%, #1e1b4b 100%)",
    iconColor: "#60a5fa"
  },
  {
    name: "Mare del Sud",
    desc_it: "Vortici e abissi che inghiottono navi e tempo.",
    desc_en: "Whirlpools and abysses that engulf ships and time.",
    npcs: ["Marinaio Barnaby", "Calamaro Elettrico", "Drifter AI"],
    x: 50,
    y: 80,
    bg: "linear-gradient(135deg, #311042 0%, #1e1b4b 100%)",
    iconColor: "#c084fc"
  },
  {
    name: "Oceano di Thalassor",
    desc_it: "Anomalie magnetiche e imponenti correnti rotanti.",
    desc_en: "Magnetic anomalies and impressive rotating currents.",
    npcs: ["Kora la Contrabbandiera", "Leviathan-0", "Sgorbio di Rame"],
    x: 5,
    y: 50,
    bg: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)",
    iconColor: "#2dd4bf"
  },
  {
    name: "Oceano di Erebus",
    desc_it: "Tempeste perpetue e grandi vortici magnetici creano onde anomale e interferenze tecnologiche.",
    desc_en: "Perpetual storms and great magnetic vortexes create rogue waves and technological interference.",
    npcs: ["Capitano Barbarossa", "Sirena di Silicio", "Mastro Gid"],
    x: 95,
    y: 5,
    bg: "linear-gradient(135deg, #450a0a 0%, #311042 100%)",
    iconColor: "#f87171"
  }
];

export default function WorldMap({ onTravelComplete }) {
  const { game, generateMapImage, travel } = useGame();
  const [generating, setGenerating] = useState(false);
  const [travelingLoc, setTravelingLoc] = useState(null);
  useTabMusic('map');

  if (!game) return null;

  const lang = game.language || "en";
  const mapData = game.map_data || { locations: [], current_location: null };
  const locations = mapData.locations || [];
  const currentLocation = mapData.current_location;
  const isSharedWorld = game.setting === "shared_world";

  // Check if a location is discovered by name
  const isDiscovered = (name) => {
    return locations.some(loc => loc.name === name);
  };

  const handleGenerateMap = async () => {
    if (locations.length === 0) {
      toast.error(t(lang, 'explore_more'));
      return;
    }
    setGenerating(true);
    try {
      await generateMapImage(game.id);
      toast.success(t(lang, 'world_map') + "!");
    } catch {
      toast.error("Failed to generate map");
    } finally {
      setGenerating(false);
    }
  };

  const handleTravel = async (locationName) => {
    setTravelingLoc(locationName);
    toast.info(lang.startsWith("it") ? `In viaggio verso ${locationName}...` : `Traveling to ${locationName}...`);
    try {
      await travel(game.id, locationName);
      toast.success(lang.startsWith("it") ? `Arrivato a ${locationName}!` : `Arrived at ${locationName}!`);
      if (onTravelComplete) {
        onTravelComplete();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Travel failed");
    } finally {
      setTravelingLoc(null);
    }
  };

  if (isSharedWorld) {
    return (
      <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="world-map">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 gap-2 border-b border-[#cfb53b]/20 pb-4">
            <div>
              <h2 className="font-heading text-lg sm:text-2xl text-[#cfb53b] flex items-center gap-2">
                <Navigation size={22} className="animate-pulse" />
                {lang.startsWith("it") ? "Mappa del Mondo Unico" : "Shared World Map"}
              </h2>
              <p className="text-xs text-[#94a3b8] mt-1">
                {lang.startsWith("it") 
                  ? "Seleziona una delle aree scoperte per viaggiare. Le aree inesplorate sono nascoste dalle nubi."
                  : "Select a discovered area to travel there. Unexplored regions are hidden by clouds."}
              </p>
            </div>
            <div className="flex items-center gap-1.5 bg-[#cfb53b]/10 border border-[#cfb53b]/30 px-3 py-1.5 rounded-full text-xs text-[#cfb53b] font-heading shrink-0">
              <Compass size={14} className="animate-spin-slow" />
              <span>{lang.startsWith("it") ? "Posizione: " : "Current: "}{currentLocation}</span>
            </div>
          </div>

          {/* Interactive Map Grid */}
          <div className="relative w-full aspect-[4/3] max-h-[500px] bg-[#0c0c0e] rounded-xl border border-[#cfb53b]/10 overflow-hidden shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] mb-8">
            {/* Grid overlay for classical cartographic look */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#cfb53b05_1px,transparent_1px),linear-gradient(to_bottom,#cfb53b05_1px,transparent_1px)] bg-[size:40px_40px]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(10,10,12,0.95)_100%)] pointer-events-none" />

            {/* Render landmarks */}
            {SHARED_WORLD_LOCATIONS.map((loc, idx) => {
              const discovered = isDiscovered(loc.name);
              const isCurrent = loc.name === currentLocation;
              const isTraveling = travelingLoc === loc.name;

              return (
                <div
                  key={idx}
                  style={{ left: `${loc.x}%`, top: `${loc.y}%` }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group z-25"
                >
                  {/* Pin Node */}
                  <div className="relative flex flex-col items-center">
                    {/* Ring Pulse for current location */}
                    {isCurrent && (
                      <span className="absolute -inset-2 rounded-full bg-[#cfb53b]/20 border border-[#cfb53b]/40 animate-ping pointer-events-none" />
                    )}

                    <button
                      onClick={() => discovered && !isCurrent && handleTravel(loc.name)}
                      disabled={!discovered || isCurrent || travelingLoc !== null}
                      style={{ 
                        background: discovered ? loc.bg : "#1a1a1d", 
                        borderColor: isCurrent ? "#cfb53b" : discovered ? `${loc.iconColor}50` : "#2d3748"
                      }}
                      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${
                        discovered ? 'cursor-pointer' : 'cursor-not-allowed opacity-80'
                      }`}
                    >
                      {isTraveling ? (
                        <Loader2 className="animate-spin text-[#cfb53b]" size={20} />
                      ) : !discovered ? (
                        <Lock size={18} className="text-[#64748b]" />
                      ) : (
                        <MapPin size={22} style={{ color: isCurrent ? "#cfb53b" : loc.iconColor }} className={isCurrent ? "animate-bounce" : ""} />
                      )}
                    </button>

                    {/* Landmark Name Label */}
                    <span className={`mt-2 font-heading text-[10px] sm:text-xs px-2 py-0.5 rounded-full border bg-[#0a0a0c]/90 tracking-wide transition-colors ${
                      isCurrent 
                        ? 'border-[#cfb53b] text-[#cfb53b]' 
                        : discovered 
                          ? 'border-[#2d3748] text-[#e2e8f0] group-hover:text-[#cfb53b] group-hover:border-[#cfb53b]/50' 
                          : 'border-transparent text-[#475569]'
                    }`}>
                      {discovered ? loc.name : "???"}
                    </span>

                    {/* Popover detailed tooltip */}
                    <div className="absolute bottom-full mb-3 hidden group-hover:flex flex-col w-56 p-3 bg-[#16161a]/95 border border-[#cfb53b]/30 rounded-lg shadow-2xl backdrop-blur-md z-50 pointer-events-none animate-fadeIn">
                      <h4 className="font-heading text-xs text-[#cfb53b] flex items-center gap-1.5">
                        {discovered ? loc.name : "???"}
                        {isCurrent && <span className="text-[9px] bg-[#cfb53b]/20 px-1.5 py-0.5 rounded-full">{lang.startsWith("it") ? "Qui" : "Here"}</span>}
                      </h4>
                      <p className="text-[10px] text-[#94a3b8] mt-1.5 leading-relaxed">
                        {discovered 
                          ? (lang.startsWith("it") ? loc.desc_it : loc.desc_en) 
                          : (lang.startsWith("it") ? "Regione inesplorata coperta da una fitta coltre di nubi." : "Unexplored region shrouded in thick mist.")}
                      </p>
                      {discovered && (
                        <div className="mt-2 pt-2 border-t border-[#2d3748] flex flex-col gap-1">
                          <span className="text-[9px] text-[#cfb53b] flex items-center gap-1">
                            <Users size={10} /> {lang.startsWith("it") ? "Abitanti:" : "Residents:"}
                          </span>
                          <span className="text-[9px] text-slate-300 font-medium">
                            {loc.npcs.join(", ")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Fog of War Cloud Overlays */}
            {SHARED_WORLD_LOCATIONS.map((loc, idx) => {
              const discovered = isDiscovered(loc.name);
              if (discovered) return null;

              return (
                <div
                  key={`cloud-${idx}`}
                  style={{ 
                    left: `${loc.x}%`, 
                    top: `${loc.y}%`,
                    width: '180px',
                    height: '180px'
                  }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none mix-blend-screen opacity-70 animate-pulse-slow"
                >
                  <div className="w-full h-full rounded-full bg-gradient-to-r from-slate-700/20 via-slate-500/10 to-transparent blur-2xl" />
                  <Cloud size={96} className="absolute inset-0 m-auto text-slate-500/20 blur-[6px] animate-drift" />
                </div>
              );
            })}
          </div>

          {/* Location Cards List */}
          <div className="space-y-4">
            <h3 className="font-heading text-sm sm:text-base text-[#cfb53b] uppercase tracking-wider">
              {lang.startsWith("it") ? "Luoghi del Mondo" : "World Locations"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SHARED_WORLD_LOCATIONS.map((loc, idx) => {
                const discovered = isDiscovered(loc.name);
                const isCurrent = loc.name === currentLocation;
                const isTraveling = travelingLoc === loc.name;

                return (
                  <div
                    key={`card-${idx}`}
                    className={`relative overflow-hidden rounded-xl border p-4 transition-all ${
                      !discovered 
                        ? 'border-slate-800 bg-[#0e0e10]/60' 
                        : isCurrent
                          ? 'border-[#cfb53b]/40 bg-[#cfb53b]/5'
                          : 'border-slate-800 bg-[#16161a]/80 hover:border-slate-700'
                    }`}
                  >
                    {/* Background biome gradient hint */}
                    {discovered && (
                      <div 
                        style={{ background: loc.bg }} 
                        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
                      />
                    )}

                    <div className="flex gap-4">
                      {/* Node Avatar */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 border ${
                        !discovered
                          ? 'bg-[#1e1e24]/50 border-slate-800'
                          : isCurrent
                            ? 'bg-[#cfb53b]/10 border-[#cfb53b]/30'
                            : 'bg-slate-800/40 border-slate-700'
                      }`}>
                        {isTraveling ? (
                          <Loader2 className="animate-spin text-[#cfb53b]" size={20} />
                        ) : !discovered ? (
                          <Lock size={18} className="text-[#475569]" />
                        ) : (
                          <MapPin size={20} style={{ color: isCurrent ? "#cfb53b" : loc.iconColor }} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-heading text-sm sm:text-base ${discovered ? 'text-[#e2e8f0]' : 'text-slate-600'}`}>
                            {discovered ? loc.name : "???"}
                          </h4>
                          {isCurrent && (
                            <span className="text-[10px] bg-[#cfb53b]/20 text-[#cfb53b] px-2 py-0.5 rounded-full font-heading font-medium">
                              {lang.startsWith("it") ? "Attuale" : "Current"}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-[#94a3b8] mt-1 line-clamp-2">
                          {discovered
                            ? (lang.startsWith("it") ? loc.desc_it : loc.desc_en)
                            : (lang.startsWith("it") ? "Quest'area è coperta da dense nubi protettive. Avanza nella storia per rivelarne la rotta." : "This region is covered by protective mists. Advance the story to find the path.")}
                        </p>

                        {discovered && (
                          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#64748b]">
                            <span className="flex items-center gap-1 shrink-0">
                              <Users size={12} className="text-[#cfb53b]/70" />
                              <strong className="text-[#94a3b8]">{lang.startsWith("it") ? "NPC:" : "NPCs:"}</strong> {loc.npcs.join(", ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action button */}
                    {discovered && !isCurrent && (
                      <div className="mt-4 flex justify-end">
                        <Button
                          disabled={travelingLoc !== null}
                          onClick={() => handleTravel(loc.name)}
                          size="sm"
                          className="bg-[#cfb53b] hover:bg-[#d4af37] text-black font-heading font-bold text-xs"
                        >
                          {isTraveling ? (
                            <span className="flex items-center gap-1.5">
                              <Loader2 className="animate-spin" size={12} />
                              {lang.startsWith("it") ? "In Viaggio..." : "Traveling..."}
                            </span>
                          ) : (
                            lang.startsWith("it") ? "Viaggia Qui" : "Travel Here"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Backward compatibility fallback for procedural game modes
  const mapImageUrl = mapData.image_url;
  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="world-map">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Navigation size={20} /> {t(lang, 'world_map')}
          </h2>
          <Button
            data-testid="generate-map-button"
            onClick={handleGenerateMap}
            disabled={generating || locations.length === 0}
            variant="outline"
            size="sm"
            className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 text-xs sm:text-sm"
          >
            {generating ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw size={14} className="mr-2" />}
            {t(lang, 'generate_map')}
          </Button>
        </div>

        {mapImageUrl && (
          <div className="map-container rounded-lg overflow-hidden mb-6" data-testid="map-image">
            <img
              src={resolveImageUrl(mapImageUrl, BACKEND_URL)}
              alt="World Map"
              className="w-full max-h-[400px] object-contain filter sepia-[0.3] contrast-[1.1]"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {locations.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#cfb53b]/20 rounded-lg">
            <MapPin size={48} className="mx-auto text-[#cfb53b]/20 mb-4" />
            <p className="text-[#94a3b8]">{t(lang, 'no_locations')}</p>
            <p className="text-[#64748b] text-sm mt-1">{t(lang, 'explore_more')}</p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children" data-testid="locations-list">
            {locations.map((loc, idx) => {
              const isCurrent = loc.name === currentLocation;
              return (
                <div
                  key={idx}
                  data-testid={`location-${idx}`}
                  className={`map-location flex items-center gap-4 bg-[#1a1a1d]/80 border rounded-lg p-4 animate-fadeInUp ${
                    isCurrent
                      ? 'border-[#cfb53b]/40 bg-[#cfb53b]/5 current'
                      : 'border-[#2d3748]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isCurrent ? 'bg-[#cfb53b]/20' : 'bg-[#2d3748]'
                  }`}>
                    <MapPin size={18} className={isCurrent ? 'text-[#cfb53b]' : 'text-[#64748b]'} />
                  </div>
                  <div>
                    <h4 className={`font-heading text-sm ${isCurrent ? 'text-[#cfb53b]' : 'text-[#e2e8f0]'}`}>
                      {loc.name}
                      {isCurrent && <span className="ml-2 text-xs bg-[#cfb53b]/20 text-[#cfb53b] px-2 py-0.5 rounded-full">{t(lang, 'current_location')}</span>}
                    </h4>
                    <p className="text-xs text-[#94a3b8]">{loc.description}</p>
                  </div>
                  <div className="ml-auto text-xs text-[#64748b]">
                    #{idx + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
